import { jsxs, jsx } from "react/jsx-runtime";
import { memo, useState, useMemo, useCallback, useEffect } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import { IconArrowRight } from "./ConciergeIcons.js";
const ConciergeTrendingCard = ({
  imageUrl,
  title,
  onClick,
  isActive = true
}) => {
  return /* @__PURE__ */ jsxs(
    motion.div,
    {
      className: "tw:relative tw:cursor-pointer tw:shrink-0 tw:rounded-[10px] tw:overflow-hidden",
      style: { width: "224px", height: "400px" },
      onClick,
      whileHover: { scale: 1.02 },
      whileTap: { scale: 0.98 },
      animate: {
        opacity: isActive ? 1 : 0.5,
        scale: isActive ? 1 : 0.95
      },
      transition: { duration: 0.3, ease: "easeOut" },
      children: [
        /* @__PURE__ */ jsx("img", { src: imageUrl, alt: title, className: "tw:absolute tw:inset-0 tw:w-full tw:h-full tw:object-cover" }),
        /* @__PURE__ */ jsx(
          "div",
          {
            className: "tw:absolute tw:bottom-0 tw:left-0 tw:right-0 tw:h-[60%]",
            style: {
              background: "linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0) 10%, rgba(255, 255, 255, 0.15) 25%, rgba(255, 255, 255, 0.4) 40%, rgba(255, 255, 255, 0.7) 55%, rgba(255, 255, 255, 0.9) 70%, rgba(255, 255, 255, 1) 85%)"
            }
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: "tw:absolute tw:top-3 tw:left-2 tw:right-2 tw:flex tw:items-center tw:justify-between", children: [
          /* @__PURE__ */ jsx(
            "div",
            {
              className: "tw:px-3 tw:py-1.5 tw:rounded-full tw:text-[12px] tw:font-semibold tw:leading-[20px] tw:tracking-[0.08px] tw:text-primary tw:border tw:border-white",
              style: {
                backgroundColor: "rgba(255, 255, 255, 0.6)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)"
              },
              children: "Explore now"
            }
          ),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: "tw:w-8 tw:h-8 tw:rounded-full tw:flex tw:items-center tw:justify-center tw:border tw:border-white",
              style: {
                backgroundColor: "rgba(255, 255, 255, 0.6)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)"
              },
              children: /* @__PURE__ */ jsx(IconArrowRight, { size: 14 })
            }
          )
        ] }),
        /* @__PURE__ */ jsx("div", { className: "tw:absolute tw:bottom-4 tw:left-3 tw:right-3", children: /* @__PURE__ */ jsx("p", { className: "tw:text-[20px] tw:font-semibold tw:leading-[28px] tw:tracking-[0.08px] tw:text-primary", children: title }) })
      ]
    }
  );
};
const CarouselNavButton = ({ direction, onClick, disabled }) => {
  return /* @__PURE__ */ jsx(
    motion.button,
    {
      className: "tw:absolute tw:top-1/2 tw:-translate-y-1/2 tw:z-10 tw:w-10 tw:h-10 tw:rounded-full tw:flex tw:items-center tw:justify-center tw:border tw:border-white tw:shadow-lg",
      style: {
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        [direction === "prev" ? "left" : "right"]: "8px"
      },
      onClick,
      disabled,
      whileHover: { scale: 1.1 },
      whileTap: { scale: 0.95 },
      animate: { opacity: disabled ? 0.3 : 1 },
      transition: { duration: 0.2 },
      children: /* @__PURE__ */ jsx(
        "svg",
        {
          width: "20",
          height: "20",
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: "currentColor",
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round",
          style: { transform: direction === "prev" ? "rotate(180deg)" : "none" },
          children: /* @__PURE__ */ jsx("path", { d: "M9 18l6-6-6-6" })
        }
      )
    }
  );
};
const ConciergeTrendingSearchesComponent = ({
  suggestedQueries,
  onSearch
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    dragFree: true
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);
  const trendingData = useMemo(() => {
    return suggestedQueries.filter((item) => item.imageUrl && item.imageUrl.trim() !== "").map((item, index) => ({
      id: `query-${index}`,
      imageUrl: item.imageUrl,
      title: item.query
    }));
  }, [suggestedQueries]);
  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);
  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);
  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);
  if (trendingData.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsxs("div", { className: "tw:w-full tw:mt-8 tw:max-w-[1200px] tw:mx-auto", children: [
    /* @__PURE__ */ jsx(
      motion.p,
      {
        className: "tw:text-center tw:text-[16px] tw:font-semibold tw:leading-[32px] tw:tracking-[0.08px] tw:text-primary tw:mb-6 tw:px-4",
        initial: { opacity: 0, y: 10 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.4 },
        children: "A few suggestions to get you started"
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: "tw:relative tw:px-12", children: [
      canScrollPrev && /* @__PURE__ */ jsx(CarouselNavButton, { direction: "prev", onClick: scrollPrev, disabled: !canScrollPrev }),
      canScrollNext && /* @__PURE__ */ jsx(CarouselNavButton, { direction: "next", onClick: scrollNext, disabled: !canScrollNext }),
      /* @__PURE__ */ jsx("div", { ref: emblaRef, className: "tw:overflow-x-clip tw:overflow-y-visible tw:py-2", children: /* @__PURE__ */ jsx(
        motion.div,
        {
          className: "tw:flex tw:gap-4",
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          transition: { duration: 0.5, staggerChildren: 0.1 },
          children: trendingData.map((item, index) => /* @__PURE__ */ jsx(
            motion.div,
            {
              className: "tw:shrink-0",
              initial: { opacity: 0, x: 20 },
              animate: { opacity: 1, x: 0 },
              transition: { duration: 0.4, delay: index * 0.1 },
              children: /* @__PURE__ */ jsx(ConciergeTrendingCard, { imageUrl: item.imageUrl, title: item.title, onClick: () => onSearch(item.title) })
            },
            item.id
          ))
        }
      ) })
    ] })
  ] });
};
const ConciergeTrendingSearches = memo(ConciergeTrendingSearchesComponent);
export {
  ConciergeTrendingSearches
};
//# sourceMappingURL=ConciergeTrendingSearches.js.map
